import { v4 as uuidv4 } from 'uuid';
import { syncActiveMapState } from './gameStateManager';

/**
 * Sort initiative order by initiative value (highest to lowest).
 * Undefined initiative values are placed at the end.
 */
export const sortInitiativeOrder = (order) => {
  return [...order].sort((a, b) => {
    if (a.initiative === undefined && b.initiative === undefined) return 0;
    if (a.initiative === undefined) return 1;
    if (b.initiative === undefined) return -1;
    return b.initiative - a.initiative;
  });
};

/**
 * Create a base coin/token object with default properties.
 */
export const createBaseCoin = ({
  color,
  isEnemy = false,
  image = null,
  isLarge = false,
  name = '',
  maxHealth,
  armorClass,
  level,
  isPc,
  initiative,
  x,
  y,
  removed = true,
  hidden = false,
  statuses = [],
  monsterData = null,
}) => {
  const coin = {
    id: uuidv4(),
    x,
    y,
    color,
    isEnemy,
    image,
    isLarge,
    name,
    maxHealth,
    currentHealth: maxHealth,
    armorClass,
    initiative,
    statuses,
    removed,
    hidden,
  };

  if (typeof level === 'number' && level >= 1 && level <= 20) {
    coin.level = level;
  }
  if (!isEnemy && typeof isPc === 'boolean') {
    coin.isPc = isPc;
  }

  if (monsterData) {
    coin.monsterData = monsterData;
  }

  return coin;
};

/**
 * Size string to isLarge boolean mapping for monsters.
 */
export const MONSTER_SIZE_MAP = {
  'Tiny': false,
  'Small': false,
  'Medium': false,
  'Large': true,
  'Huge': true,
  'Gargantuan': true,
};

/**
 * Add new coins to state and sync with socket if on active map.
 * Returns the updated coins array and initiative order.
 */
export const addCoinsToState = ({
  newCoins,
  currentCoins,
  currentInitiativeOrder,
  roomId,
  viewingMapId,
  currentMapId,
  socketHook,
  setCoins,
  setInitiativeOrder,
}) => {
  const updatedCoins = [...currentCoins, ...newCoins];
  setCoins(updatedCoins);
  
  const updatedOrder = sortInitiativeOrder([...currentInitiativeOrder, ...newCoins]);
  setInitiativeOrder(updatedOrder);
  
  syncActiveMapState({
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
    patch: {
      coins: updatedCoins,
      initiativeOrder: updatedOrder,
    },
  });
  
  return { updatedCoins, updatedOrder };
};

/**
 * Create coins from an encounter's monster list.
 */
export const createCoinsFromEncounter = (encounter) => {
  if (!encounter || !encounter.monsters || encounter.monsters.length === 0) {
    return [];
  }
  
  const newCoins = [];
  
  encounter.monsters.forEach(monster => {
    const count = monster.count || 1;
    
    for (let i = 0; i < count; i++) {
      const coin = createBaseCoin({
        color: '#8B0000',
        isEnemy: true,
        image: monster.img || null,
        isLarge: MONSTER_SIZE_MAP[monster.size] || false,
        name: count > 1 ? `${monster.name} ${i + 1}` : monster.name,
        maxHealth: monster.hp,
        armorClass: monster.ac,
        monsterData: {
          index: monster.index,
          cr: monster.cr,
          xp: monster.xp,
          type: monster.type,
          role: monster.role,
        },
      });
      
      newCoins.push(coin);
    }
  });
  
  return newCoins;
};

/**
 * Copy a coin with a new ID and reset position/state.
 */
export const copyCoin = (coin) => {
  return {
    ...coin,
    id: uuidv4(),
    x: undefined,
    y: undefined,
    currentHealth: coin.maxHealth,
    name: coin.name || '',
    statuses: [],
    removed: true,
    hidden: false,
  };
};
